'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import toast from 'react-hot-toast';

interface RecentDocument {
  id: string;
  type: 'change_order' | 'contract' | 'photo';
  name: string;
  jobName: string;
  date: string;
  size?: string;
}

interface CustomerContact {
  id: string;
  name: string;
  jobName: string;
  phone: string;
  email?: string;
  status: string;
}

interface JobMilestone {
  id: string;
  jobName: string;
  customer: string;
  milestones: {
    permit: boolean;
    materialDelivery: boolean;
    installStart: boolean;
    qcPass: boolean;
    finalPayment: boolean;
  };
  progress: number;
}

const MILESTONE_LABELS: { key: keyof JobMilestone['milestones']; label: string; icon: string }[] = [
  { key: 'permit', label: 'Permit', icon: 'DocumentTextIcon' },
  { key: 'materialDelivery', label: 'Materials', icon: 'TruckIcon' },
  { key: 'installStart', label: 'Install Start', icon: 'WrenchScrewdriverIcon' },
  { key: 'qcPass', label: 'QC Pass', icon: 'CheckBadgeIcon' },
  { key: 'finalPayment', label: 'Final Payment', icon: 'CurrencyDollarIcon' },
];

const DEMO_DOCS: RecentDocument[] = [];

const DEMO_CONTACTS: CustomerContact[] = [];

const DEMO_MILESTONES: JobMilestone[] = [];

export default function QuickAccessWidget() {
  const [activeTab, setActiveTab] = useState<'docs' | 'contacts' | 'milestones'>('docs');
  const [documents, setDocuments] = useState<RecentDocument[]>(DEMO_DOCS);
  const [contacts, setContacts] = useState<CustomerContact[]>(DEMO_CONTACTS);
  const [milestones, setMilestones] = useState<JobMilestone[]>(DEMO_MILESTONES);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  const supabase = createClient();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load pm_documents
      const { data: pmDocs } = await supabase
        .from('pm_documents')
        .select('id, job_id, job_address, customer_name, document_type, document_name, file_size, uploaded_at')
        .eq('assigned_pm_id', 'pm-john-001')
        .order('uploaded_at', { ascending: false })
        .limit(6);

      if (pmDocs && pmDocs.length > 0) {
        const docs: RecentDocument[] = pmDocs.map((d: any) => ({
          id: d.id,
          type: d.document_type === 'change_order' ? 'change_order' : d.document_type === 'photo' ? 'photo' : 'contract',
          name: d.document_name || 'Document',
          jobName: d.customer_name || d.job_address || 'Active Job',
          date: d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Recent',
          size: d.file_size || undefined,
        }));
        setDocuments(docs);
      }

      // Load customers for speed dial
      const { data: customers } = await supabase
        .from('customers')
        .select('id, first_name, last_name, phone, email, status')
        .limit(6);

      if (customers && customers.length > 0) {
        const ctcts: CustomerContact[] = customers.map((c: any) => ({
          id: c.id,
          name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Customer',
          jobName: 'Active Job',
          phone: c.phone || '555-0100',
          email: c.email,
          status: c.status || 'active',
        }));
        setContacts(ctcts);
      }

      // Load job milestones
      const { data: milestoneData } = await supabase
        .from('job_milestones')
        .select('id, job_id, milestone_name, is_completed, milestone_order')
        .eq('assigned_pm_id', 'pm-john-001')
        .order('milestone_order', { ascending: true })
        .limit(25);

      if (milestoneData && milestoneData.length > 0) {
        // Group by job_id
        const jobMap: Record<string, any[]> = {};
        milestoneData.forEach((m: any) => {
          if (!jobMap[m.job_id]) jobMap[m.job_id] = [];
          jobMap[m.job_id].push(m);
        });

        // Load jobs for names
        const jobIds = Object.keys(jobMap);
        const { data: jobsData } = await supabase
          .from('jobs')
          .select('id, customer')
          .in('id', jobIds);

        const jobNameMap: Record<string, string> = {};
        (jobsData || []).forEach((j: any) => { jobNameMap[j.id] = j.customer; });

        const mstones: JobMilestone[] = jobIds.slice(0, 5).map(jobId => {
          const jMilestones = jobMap[jobId];
          const sorted = jMilestones.sort((a, b) => a.milestone_order - b.milestone_order);
          const completed = sorted.filter(m => m.is_completed).length;
          const ms = {
            permit: sorted[0]?.is_completed ?? false,
            materialDelivery: sorted[1]?.is_completed ?? false,
            installStart: sorted[2]?.is_completed ?? false,
            qcPass: sorted[3]?.is_completed ?? false,
            finalPayment: sorted[4]?.is_completed ?? false,
          };
          return {
            id: jobId,
            jobName: jobNameMap[jobId] || `Job ${jobId.slice(0, 6)}`,
            customer: jobNameMap[jobId] || 'Customer',
            milestones: ms,
            progress: Math.round((completed / Math.max(sorted.length, 1)) * 100),
          };
        });
        if (mstones.length > 0) setMilestones(mstones);
      }
    } catch (err) {
      console.error('QuickAccessWidget load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const docTypeConfig = {
    change_order: { icon: 'DocumentTextIcon', color: 'text-warning', bg: 'bg-warning/10', label: 'Change Order' },
    contract: { icon: 'ClipboardDocumentListIcon', color: 'text-primary', bg: 'bg-primary/10', label: 'Contract' },
    photo: { icon: 'PhotoIcon', color: 'text-success', bg: 'bg-success/10', label: 'Photo' },
  };

  const tabs = [
    { id: 'docs', label: 'Documents', icon: 'FolderOpenIcon' },
    { id: 'contacts', label: 'Speed Dial', icon: 'PhoneIcon' },
    { id: 'milestones', label: 'Milestones', icon: 'CheckCircleIcon' },
  ] as const;

  const toggleMilestone = async (jobId: string, key: keyof JobMilestone['milestones']) => {
    setMilestones(prev => prev.map(j => {
      if (j.id !== jobId) return j;
      const updated = { ...j.milestones, [key]: !j.milestones[key] };
      const completed = Object.values(updated).filter(Boolean).length;
      return { ...j, milestones: updated, progress: Math.round((completed / 5) * 100) };
    }));
    toast.success('Milestone updated');
  };

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-border">
        <div className="w-7 h-7 rounded-lg bg-secondary/10 flex items-center justify-center">
          <Icon name="FolderOpenIcon" size={15} className="text-secondary" variant="solid" />
        </div>
        <h3 className="font-heading text-sm font-semibold text-foreground">Quick Access</h3>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium transition-standard ${
              activeTab === tab.id
                ? 'text-primary border-b-2 border-primary -mb-px' : 'text-muted-foreground hover:text-foreground'
            }`}
            style={{ minHeight: '44px' }}
          >
            <Icon name={tab.icon as any} size={13} variant={activeTab === tab.id ? 'solid' : 'outline'} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Recent Documents */}
            {activeTab === 'docs' && (
              <div className="space-y-2">
                {documents.map(doc => {
                  const cfg = docTypeConfig[doc.type];
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg hover:border-primary/30 hover:bg-muted transition-standard cursor-pointer group"
                    >
                      <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                        <Icon name={cfg.icon as any} size={15} className={cfg.color} variant="solid" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-xs font-medium text-foreground truncate">{doc.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="font-caption text-[10px] text-muted-foreground truncate">{doc.jobName}</span>
                          <span className="text-border">·</span>
                          <span className="font-caption text-[10px] text-muted-foreground flex-shrink-0">{doc.date}</span>
                        </div>
                      </div>
                      <span className={`font-caption text-[10px] px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color} flex-shrink-0`}>
                        {cfg.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Speed Dial */}
            {activeTab === 'contacts' && (
              <div className="space-y-2">
                {contacts.map(contact => (
                  <div
                    key={contact.id}
                    className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg hover:border-primary/30 hover:bg-muted transition-standard"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon name="UserCircleIcon" size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-xs font-semibold text-foreground truncate">{contact.name}</p>
                      <p className="font-caption text-[10px] text-muted-foreground truncate">{contact.jobName}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <a
                        href={`tel:${contact.phone}`}
                        className="w-7 h-7 rounded-full bg-success/10 hover:bg-success/20 flex items-center justify-center transition-standard"
                        title={`Call ${contact.name}`}
                      >
                        <Icon name="PhoneIcon" size={13} className="text-success" variant="solid" />
                      </a>
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="w-7 h-7 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-standard"
                          title={`Email ${contact.name}`}
                        >
                          <Icon name="EnvelopeIcon" size={13} className="text-primary" variant="solid" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Milestones */}
            {activeTab === 'milestones' && (
              <div className="space-y-3">
                {milestones.map(job => (
                  <div key={job.id} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                      className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted transition-standard"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="font-data text-[10px] font-bold text-primary">{job.progress}%</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-body text-xs font-semibold text-foreground truncate">{job.jobName}</p>
                          <p className="font-caption text-[10px] text-muted-foreground truncate">{job.customer}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${job.progress === 100 ? 'bg-success' : 'bg-primary'}`}
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <Icon
                          name={expandedJob === job.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                          size={14}
                          className="text-muted-foreground"
                        />
                      </div>
                    </button>
                    {expandedJob === job.id && (
                      <div className="p-3 space-y-1.5 bg-background">
                        {MILESTONE_LABELS.map(({ key, label, icon }) => (
                          <button
                            key={key}
                            onClick={() => toggleMilestone(job.id, key)}
                            className={`w-full flex items-center gap-2.5 p-2 rounded-md transition-standard text-left ${
                              job.milestones[key]
                                ? 'bg-success/10 border border-success/20' :'bg-muted/50 border border-border hover:border-primary/30'
                            }`}
                          >
                            <Icon
                              name={job.milestones[key] ? 'CheckCircleIcon' : (icon as any)}
                              size={14}
                              className={job.milestones[key] ? 'text-success' : 'text-muted-foreground'}
                              variant={job.milestones[key] ? 'solid' : 'outline'}
                            />
                            <span className={`font-caption text-xs ${job.milestones[key] ? 'text-success line-through' : 'text-foreground'}`}>
                              {label}
                            </span>
                            {job.milestones[key] && (
                              <span className="ml-auto font-caption text-[10px] text-success">Done</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
